import React from 'react';
import { Link } from 'react-router-dom';

function StrengthsWeaknessesGuide({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            성격의 장단점
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
            면접관이 가장 궁금해하는 질문, 똑똑하게 답변하는 법을 알려드려요! 💪
          </p>
        </div>

        {/* 면접관의 의도 파악 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🤔</span>
            면접관은 왜 이 질문을 할까요?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">🔍 자기 인식 능력 확인</h3>
              <p className="text-sm leading-relaxed">
                자신을 객관적으로 바라볼 수 있는지, 성찰 능력이 있는지 확인합니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">💎 정직성과 솔직함 평가</h3>
              <p className="text-sm leading-relaxed">
                자신의 부족한 점도 솔직하게 인정할 수 있는 정직한 성격인지 판단합니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">📈 성장 가능성 판단</h3>
              <p className="text-sm leading-relaxed">
                단점을 개선하려는 의지와 노력이 있는지, 발전 가능성을 확인합니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-50'}`}>
              <h3 className="font-bold mb-3 text-orange-600">🎯 직무 적합성 검증</h3>
              <p className="text-sm leading-relaxed">
                해당 직무에 치명적인 단점은 없는지, 장점이 업무에 도움이 될지 판단합니다.
              </p>
            </div>
          </div>
        </div>

        {/* 장단점 답변 구조 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">⚖️</span>
            성격의 장단점 답변법 (총 40초 내)
          </h2>
          
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">✨ 황금 구조 (총 40초)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-2">장점</div>
                  <span className="font-bold text-blue-600">25초</span>
                </div>
                <p className="text-sm">결론 → 간단한 사례 → 회사 기여</p>
              </div>

              <div className={`p-4 rounded-xl border-l-4 border-red-500 ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}`}>
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-2">단점</div>
                  <span className="font-bold text-red-600">15초</span>
                </div>
                <p className="text-sm">솔직한 인정 → 보완 노력</p>
              </div>
            </div>
          </div>

          {/* 완성된 답변 예시 */}
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">🎭 완성된 답변 예시 (총 40초)</h3>
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-gradient-to-r from-blue-500 to-red-500`}>
              <div className="space-y-4">
                <div>
                  <span className="font-bold text-blue-600">[장점 - 25초]</span>
                  <p className="mt-1">"제 가장 큰 장점은 집중력입니다. 대학교 때 팀 프로젝트에서 복잡한 오류가 발생했을 때, 6시간 동안 집중해서 문제를 해결했고 A+를 받았습니다. 이런 집중력으로 복잡한 업무도 끝까지 완성해내겠습니다."</p>
                </div>
                <div>
                  <span className="font-bold text-red-600">[단점 - 15초]</span>
                  <p className="mt-1">"단점은 발표를 어려워한다는 점입니다. 긴장해서 목소리가 작아지는데, 작은 모임부터 적극적으로 의견을 말하며 개선하고 있습니다."</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 장점 어필 전략 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🌟</span>
            장점 어필 전략 (25초)
          </h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <h3 className="font-bold mb-3 text-blue-600">🎯 1단계: 장점 선택 기준</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>직무와 연관성이 높은 장점</strong> - 업무에 직접 도움되는 것</li>
                <li>• <strong>구체적 사례가 있는 장점</strong> - 경험으로 증명 가능한 것</li>
                <li>• <strong>차별화되는 장점</strong> - 다른 지원자와 구별되는 것</li>
                <li>• <strong>회사가 원하는 장점</strong> - 채용공고에서 요구하는 역량</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'} border-l-4 border-green-500`}>
              <h3 className="font-bold mb-3 text-green-600">📋 2단계: 답변 구조</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-2">1</div>
                  <h4 className="font-semibold mb-1">결론부터</h4>
                  <p className="text-xs">"제 장점은 ○○입니다"</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-2">2</div>
                  <h4 className="font-semibold mb-1">간단한 사례</h4>
                  <p className="text-xs">"○○할 때 이런 성과를..."</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mx-auto mb-2">3</div>
                  <h4 className="font-semibold mb-1">회사 기여</h4>
                  <p className="text-xs">"이런 점으로 기여하겠습니다"</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 단점 말하기 전략 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎭</span>
            단점 말하기 전략 (15초)
          </h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'} border-l-4 border-red-500`}>
              <h3 className="font-bold mb-3 text-red-600">✅ 좋은 단점 선택 기준</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>직무에 치명적이지 않은 것</strong> - 업무 수행에 큰 지장 없는 것</li>
                <li>• <strong>개선 가능한 성격적 특성</strong> - 노력으로 극복할 수 있는 것</li>
                <li>• <strong>인간적이고 공감 가능한 것</strong> - 누구나 이해할 수 있는 것</li>
                <li>• <strong>현재 개선 중인 것</strong> - 보완 노력을 보여줄 수 있는 것</li>
              </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-50'}`}>
                <h3 className="font-bold mb-3 text-yellow-600">😊 추천하는 단점들</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>발표 긴장</strong> - "발표할 때 긴장해서..."</li>
                  <li>• <strong>완벽주의</strong> - "세세한 부분까지 신경써서..."</li>
                  <li>• <strong>새로운 환경 적응</strong> - "처음엔 낯을 가려서..."</li>
                  <li>• <strong>우유부단</strong> - "신중하게 생각하다 보니..."</li>
                </ul>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
                <h3 className="font-bold mb-3 text-red-600">❌ 피해야 할 단점들</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>게으름, 지각</strong> - 직무 수행에 치명적</li>
                  <li>• <strong>화를 잘 냄</strong> - 인간관계에 악영향</li>
                  <li>• <strong>거짓말</strong> - 신뢰도에 문제</li>
                  <li>• <strong>단점이 없다</strong> - 자기 인식 부족으로 보임</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 직무별 장단점 예시 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">💼</span>
            직무별 장단점 예시
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* IT 개발자 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'} border-l-4 border-blue-500`}>
              <h3 className="font-bold mb-3 text-blue-600">💻 IT 개발자</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm">장점</h4>
                  <p className="text-xs">"문제 해결에 집중하는 성격으로, 복잡한 코드 오류도 포기하지 않고 끝까지 해결합니다."</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">단점</h4>
                  <p className="text-xs">"코드에 집중하다 보면 주변 소통이 부족해질 때가 있어서, 정기적으로 팀원들과 진행상황을 공유하려 노력합니다."</p>
                </div>
              </div>
            </div>

            {/* 영업 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'} border-l-4 border-green-500`}>
              <h3 className="font-bold mb-3 text-green-600">📊 영업</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm">장점</h4>
                  <p className="text-xs">"사람들과 소통하는 것을 좋아해서, 고객의 니즈를 빠르게 파악하고 맞춤 제안을 드릴 수 있습니다."</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">단점</h4>
                  <p className="text-xs">"고객을 도와주고 싶은 마음이 커서 가끔 과도한 서비스를 제공하려 하는데, 효율성도 고려하여 균형을 맞추려 합니다."</p>
                </div>
              </div>
            </div>

            {/* 디자이너 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'} border-l-4 border-purple-500`}>
              <h3 className="font-bold mb-3 text-purple-600">🎨 디자이너</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm">장점</h4>
                  <p className="text-xs">"세심한 관찰력으로 작은 디테일까지 놓치지 않아서, 사용자 경험을 개선하는 아이디어를 많이 제안합니다."</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">단점</h4>
                  <p className="text-xs">"완벽한 결과물을 위해 수정을 많이 하다 보니 때로는 일정이 빠듯해질 때가 있어서, 우선순위를 정해서 작업하려 노력합니다."</p>
                </div>
              </div>
            </div>

            {/* 마케터 */}
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-20' : 'bg-orange-50'} border-l-4 border-orange-500`}>
              <h3 className="font-bold mb-3 text-orange-600">📈 마케터</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-sm">장점</h4>
                  <p className="text-xs">"새로운 트렌드에 민감해서 빠르게 시장 변화를 감지하고, 창의적인 마케팅 아이디어를 제안할 수 있습니다."</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">단점</h4>
                  <p className="text-xs">"아이디어가 많다 보니 가끔 우선순위 선정에 고민이 많은데, 데이터 분석을 통해 객관적으로 판단하려 노력합니다."</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 실수 방지 가이드 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">⚠️</span>
            절대 하면 안 되는 실수들
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border border-red-200`}>
              <h3 className="font-bold mb-4 text-red-600">❌ 이런 답변은 피하세요!</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>"단점이 없습니다"</strong> - 자기 인식 부족으로 보임</li>
                <li>• <strong>"완벽주의가 단점입니다"</strong> - 뻔하고 식상함</li>
                <li>• <strong>"술을 좋아해서..."</strong> - 직무와 무관하고 부정적</li>
                <li>• <strong>"화를 잘 내는 편입니다"</strong> - 협업에 치명적</li>
                <li>• <strong>"지각을 자주 해서..."</strong> - 기본적인 책임감 의심</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'} border border-green-200`}>
              <h3 className="font-bold mb-4 text-green-600">✅ 이런 답변이 좋아요!</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>솔직하고 인간적인</strong> 단점 인정</li>
                <li>• <strong>직무와 무관한</strong> 성격적 특성</li>
                <li>• <strong>누구나 공감할 수 있는</strong> 자연스러운 단점</li>
                <li>• <strong>개선 노력을 보여주는</strong> 구체적 행동</li>
                <li>• <strong>성장 의지가 담긴</strong> 진정성 있는 답변</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 실전 연습법 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            실전 연습법
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">⏱️ 시간 맞춰 연습</h3>
              <p className="text-sm leading-relaxed">
                장점 25초 + 단점 15초 = 총 40초로 타이머 맞춰서 연습하세요. 시간 내에 핵심만 전달하는 연습이 중요해요.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">📱 녹음해서 체크</h3>
              <p className="text-sm leading-relaxed">
                휴대폰으로 녹음해서 들어보세요. 말하는 속도, 톤, 자연스러움을 객관적으로 확인할 수 있어요.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">👥 피드백 받기</h3>
              <p className="text-sm leading-relaxed">
                친구나 가족에게 연습해보고 피드백을 받아보세요. 진정성 있게 들리는지 확인해보세요.
              </p>
            </div>
          </div>
        </div>

        {/* 핵심 포인트 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">💡</span>
            핵심 포인트
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-50'}`}>
              <h3 className="font-bold mb-3 text-yellow-600">🌟 장점 어필 포인트 (25초)</h3>
              <ul className="space-y-2 text-sm">
                <li>• 장점 1개 깊이 있게 준비</li>
                <li>• 간단한 사례 포함 (너무 길지 않게)</li>
                <li>• 회사 업무와 연결해서 마무리</li>
                <li>• 자신감 있는 톤으로 어필</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-pink-900 bg-opacity-30' : 'bg-pink-50'}`}>
              <h3 className="font-bold mb-3 text-pink-600">😅 단점 답변 포인트 (15초)</h3>
              <ul className="space-y-2 text-sm">
                <li>• 캐주얼하고 인간적인 단점 선택</li>
                <li>• 솔직하되 직무에 무관한 것</li>
                <li>• 보완 노력을 간결하게 설명</li>
                <li>• 완전히 고쳤다고 하지 말기</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 뒤로가기 */}
        <div className="text-center">
          <Link 
            to="/guide-success" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <span className="mr-2">←</span>
            면접성공가이드로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default StrengthsWeaknessesGuide;